import { useMemo, useState } from 'react';
import {
  Badge,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Input,
  PageHeader,
  Toolbar,
  formatMoney,
  type BadgeTone,
} from '@biovet/ui';
import { useResource } from '../lib/api.js';
import { DateWindow, useDebounced } from './parts.js';
import { companyTimezone, companyToday } from '../lib/reference-data.js';

/**
 * Dashboard → Recent Activity: what has been going on in the book.
 *
 * It was a stub — the nav item existed, `App`'s switch had no case for it, and
 * it fell through to the `Pending` placeholder — so this is new rather than a
 * replacement.
 *
 * **It is deliberately not a second audit trail.** Reports → Audit Trail is the
 * forensic view: every column the log holds, over any window, filterable, and it
 * answers "who changed this row". This answers the question somebody opening the
 * dashboard is actually asking — "what has happened lately" — so it reads as a
 * feed: grouped by day, newest first, one line per thing that was done. The
 * server does the narrowing (`document_no IS NOT NULL`, one row per document per
 * action), because an unfiltered log is nine rows per invoice and reads as noise.
 *
 * The day grouping is in the **company's** timezone, not the browser's, the same
 * rule the clock under the company name keeps. A book kept in Karachi and opened
 * from a laptop still set to London would otherwise file the evening's postings
 * under tomorrow.
 */

interface ActivityRow {
  at: string;
  who: string;
  action: string;
  entity: string;
  documentType: string | null;
  documentNo: string | null;
  amountBase: string | null;
}

/**
 * The log's action as the verb somebody would use.
 *
 * `POST` is the one that matters and the one the raw enum reads worst: "POST"
 * beside a voucher number looks like a noun. The rest are ordinary.
 */
const VERB: Record<string, string> = {
  INSERT: 'created',
  UPDATE: 'updated',
  DELETE: 'deleted',
  POST: 'posted',
  APPROVE: 'approved',
  REVERSE: 'reversed',
  CANCEL: 'cancelled',
  LOGIN: 'signed in',
  LOGOUT: 'signed out',
};

/** `sales_invoices` → `sales invoice`. What kind of thing it was. */
function noun(entity: string): string {
  return entity
    .replace(/^audit\./, '')
    .replace(/_/g, ' ')
    .replace(/s$/, '');
}

/** A tone per verb, so a deletion does not read like a save. */
function toneOf(action: string): BadgeTone {
  if (action === 'POST' || action === 'APPROVE') return 'ok';
  if (action === 'DELETE' || action === 'CANCEL' || action === 'REVERSE') return 'bad';
  if (action === 'UPDATE') return 'warn';
  return 'mute';
}

/**
 * The instant as the company sees it: its own date, and its own clock.
 *
 * `Intl.DateTimeFormat` with an explicit `timeZone` rather than arithmetic on
 * the Date — the offset is a property of the zone on that date, and a book in a
 * zone that observes daylight saving would otherwise slide by an hour for half
 * the year.
 */
function inCompanyZone(iso: string): { day: string; time: string } {
  const at = new Date(iso);
  const zone = companyTimezone();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
  const time = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(at);
  return { day: parts, time };
}

/** `2026-08-19` → `Today` / `Yesterday` / `Wed, 19 Aug 2026`. */
function dayLabel(day: string, today: string): string {
  if (day === today) return 'Today';
  const y = new Date(`${today}T00:00:00Z`);
  y.setUTCDate(y.getUTCDate() - 1);
  if (day === y.toISOString().slice(0, 10)) return 'Yesterday';
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));
}

/**
 * The window and the search box, and why both open empty.
 *
 * A feed is "lately" by definition, so it opens on the whole book — the
 * opposite of the five operational registers, which open on the current month.
 * Defaulting this to a month would show an empty screen to anybody who looks on
 * the first of one, and an empty feed is indistinguishable from a book nothing
 * has happened in. The controls are here for the other question — "what
 * happened the week the March figures moved" — which used to have no answer
 * short of the audit trail's forensic view.
 *
 * Both narrow **in SQL**, not over `rows`: the feed is the newest 200 documents
 * of a log that only grows, so filtering here would search the page rather than
 * the book.
 */
export function RecentActivityScreen() {
  const [range, setRange] = useState({ from: '', to: '' });
  const [search, setSearch] = useState('');
  const settled = useDebounced(search);
  const { data, error, loading } = useResource<{ rows: ActivityRow[] }>('/recent-activity', {
    from: range.from || undefined,
    to: range.to || undefined,
    q: settled.trim() || undefined,
    limit: '200',
  });
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const today = companyToday();
  const narrowed = Boolean(range.from || range.to || settled.trim());

  // Grouped in render rather than by the server: the grouping is presentation
  // and it depends on the company's timezone, which the browser already knows
  // from `/api/context`. Same call the sales-by-zone report makes about its
  // zone header rows.
  const days = useMemo(() => {
    const out: Array<{ day: string; rows: Array<ActivityRow & { time: string }> }> = [];
    for (const r of rows) {
      const { day, time } = inCompanyZone(r.at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push({ ...r, time });
      else out.push({ day, rows: [{ ...r, time }] });
    }
    return out;
  }, [rows]);

  return (
    <>
      <PageHeader
        breadcrumb="Dashboard / Recent Activity"
        title="Recent activity"
        description="What has been entered, posted and changed lately, newest first."
        actions={
          <Input
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Document no., user, type…"
            aria-label="Search recent activity"
            className="w-[240px]"
          />
        }
      />

      <Card flush className="mb-3.5" data-noprint="1">
        <Toolbar>
          <DateWindow range={range} onChange={setRange} label="All time" />
          <div className="flex-1" />
          {/* What is on screen, said out loud. A feed showing one week looks
              exactly like a week with nothing before it. */}
          <span className="text-[12px] text-[var(--ink-3)]">
            {rows.length} entr{rows.length === 1 ? 'y' : 'ies'}{' '}
            {range.from || range.to
              ? `dated ${range.from || 'the start of the book'} to ${range.to || 'today'}`
              : 'in the whole book'}
            {settled.trim() ? ` · matching “${settled.trim()}”` : ''}
          </span>
        </Toolbar>
      </Card>

      {error && (
        <Callout tone="danger" title="Could not read the activity log">
          {error}
        </Callout>
      )}

      {!error && !loading && rows.length === 0 && narrowed && (
        <Callout tone="info" title="Nothing matches">
          No document was entered, posted or changed in this window. Widen the dates, or clear the
          search.
        </Callout>
      )}

      {!error && !loading && rows.length === 0 && !narrowed && (
        <Callout tone="info" title="Nothing has happened yet">
          Every voucher, invoice and stock movement is recorded here as it is entered. This book has
          none yet.
        </Callout>
      )}

      {days.map((group) => (
        <Card key={group.day} flush className="mb-3.5">
          <CardHeader
            title={dayLabel(group.day, today)}
            subtitle={`${group.rows.length} entr${group.rows.length === 1 ? 'y' : 'ies'}`}
          />
          <CardBody className="flex flex-col gap-0">
            {group.rows.map((r, i) => (
              <div
                key={`${r.at}-${r.documentNo}-${i}`}
                className="flex items-baseline gap-3 border-b border-[var(--line)] py-2 last:border-0"
              >
                <span className="bb-mono w-[46px] shrink-0 text-[12.5px] text-[var(--ink-3)]">
                  {r.time}
                </span>
                <span className="w-[150px] shrink-0 truncate text-[13px]">{r.who}</span>
                <Badge tone={toneOf(r.action)}>{VERB[r.action] ?? r.action.toLowerCase()}</Badge>
                <span className="bb-mono shrink-0 text-[12.5px]">{r.documentNo}</span>
                <span className="flex-1 truncate text-[12.5px] text-[var(--ink-2)]">
                  {r.documentType ?? noun(r.entity)}
                </span>
                {/* Absent rather than zero: the log records an amount only for
                    the documents that have one, and printing 0.00 against a
                    stock transfer would be a figure that means nothing. */}
                <span className="bb-mono shrink-0 text-[12.5px]">
                  {r.amountBase === null ? '' : formatMoney(r.amountBase, { decimals: 0 })}
                </span>
              </div>
            ))}
          </CardBody>
        </Card>
      ))}
    </>
  );
}
